// @flow

import { SIMPLE } from '@localytics/redux-simple-api-middleware/lib/constants';
import { apiActionTypes as Types, apiTypes } from '../constants';
import * as ApiUtils from '../util/ApiUtils';
import { ORCH_ENDPOINT_PATH } from '../config';
import {
  defaultConfig,
  VERSION_STATE_MAP,
  type OrchestrationVersion
} from '../constants/ApiTypes';

export const HEADERS = {
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
  'content-type': 'application/json'
};

export const getOrchestrations = (orgId: number) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration?full=true`,
      types: [Types.ORCHS_REQUEST, Types.ORCHS_SUCCESS, Types.ORCHS_FAILURE],
      headers: HEADERS
    }
  });

export const getUserCounts = (
  orgId: number,
  orchestrationIds: Array<number>
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/user_counts`,
      types: [
        Types.USER_COUNTS_REQUEST,
        Types.USER_COUNTS_SUCCESS,
        Types.USER_COUNTS_FAILURE
      ],
      headers: HEADERS,
      method: 'POST',
      body: {
        orchestration_ids: orchestrationIds
      }
    }
  });

export const getOrchestrationVersions = (
  orgId: number,
  orchestrationId: number
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version`,
      types: [
        Types.ORCH_VERSIONS_REQUEST,
        Types.ORCH_VERSIONS_SUCCESS,
        Types.ORCH_VERSIONS_FAILURE
      ],
      headers: HEADERS,
      payload: { orchestrationId }
    }
  });

export const getOrchestrationVersion = (
  orgId: number,
  orchestrationId: number,
  versionId: number
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}`,
      types: [
        Types.ORCH_VERSION_REQUEST,
        Types.ORCH_VERSION_SUCCESS,
        Types.ORCH_VERSION_FAILURE
      ],
      headers: HEADERS,
      payload: { orchestrationId, versionId }
    }
  });

export const getOrchestrationVersionCreatives = (
  orgId: number,
  orchestrationId: number,
  versionId: number
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}/creatives`,
      types: [
        Types.ORCH_VER_CREATIVES_REQUEST,
        Types.ORCH_VER_CREATIVES_SUCCESS,
        Types.ORCH_VER_CREATIVES_FAILURE
      ],
      headers: HEADERS,
      payload: { orchestrationId, versionId }
    }
  });

export const createOrchestration = (orgId: number, name: string) => (
  dispatch: Function
) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration`,
      types: [
        Types.CREATE_ORCH_REQUEST,
        Types.CREATE_ORCH_SUCCESS,
        Types.CREATE_ORCH_FAILURE
      ],
      headers: HEADERS,
      method: 'POST',
      body: {
        name,
        ...defaultConfig
      },
      payload: { name }
    }
  });

export const createOrchestrationVersion = (
  orgId: number,
  o: apiTypes.orchestrationVersion
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${o.orchestration_id}/version`,
      types: [
        Types.CREATE_ORCH_VER_REQUEST,
        Types.CREATE_ORCH_VER_SUCCESS,
        Types.CREATE_ORCH_VER_FAILURE
      ],
      headers: HEADERS,
      method: 'POST',
      body: {
        nodes: o.nodes,
        apps: o.apps
      },
      payload: {
        orchestrationId: o.orchestration_id,
        nodes: o.nodes,
        apps: o.apps
      }
    }
  });

export const createOrchestrationVersionCreative = (
  orgId: number,
  orchestrationId: number,
  versionId: number,
  creativeType: string,
  data: { [any]: any }
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}/creatives`,
      types: [
        Types.CREATE_ORCH_VER_CREATIVE_REQUEST,
        Types.CREATE_ORCH_VER_CREATIVE_SUCCESS,
        Types.CREATE_ORCH_VER_CREATIVE_FAILURE
      ],
      headers: HEADERS,
      method: 'POST',
      body: {
        creative_type: creativeType,
        data
      },
      payload: { orchestrationId, versionId, creativeType, data }
    }
  });

export const deleteOrchestration = (orgId: number, orchestrationId: number) => (
  dispatch: Function
) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}`,
      types: [
        Types.DELETE_ORCH_REQUEST,
        Types.DELETE_ORCH_SUCCESS,
        Types.DELETE_ORCH_FAILURE
      ],
      headers: HEADERS,
      method: 'DELETE',
      payload: { orchestrationId }
    }
  });

export const updateOrchestrationName = (
  orgId: number,
  orchestrationId: number,
  name: string
) => (dispatch: Function, getState: Function) => {
  const {
    api: { orchestrationsById: { [orchestrationId]: orch } }
  } = getState();
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}`,
      types: [
        Types.UPDATE_ORCH_NAME_REQUEST,
        Types.UPDATE_ORCH_NAME_SUCCESS,
        Types.UPDATE_ORCH_NAME_FAILURE
      ],
      headers: HEADERS,
      method: 'PUT',
      body: {
        ...orch,
        name
      },
      payload: { orchestrationId, name }
    }
  });
};

export const updateOrchestrationVersionEntryEvent = (
  orgId: number,
  orchestrationId: number,
  versionId: number,
  events: Array<{ name: string, olap_id: number }>,
  eventMappingId: number
) => (dispatch: Function, getState: Function) => {
  const {
    api: {
      versionsByOrchestrationId: { [orchestrationId]: { [versionId]: version } }
    }
  } = getState();
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}`,
      types: [
        Types.UPDATE_ORCH_EVENT_REQUEST,
        Types.UPDATE_ORCH_EVENT_SUCCESS,
        Types.UPDATE_ORCH_EVENT_FAILURE
      ],
      headers: HEADERS,
      method: 'PUT',
      body: {
        ...version,
        nodes: {
          ...version.nodes,
          '1': {
            ...version.nodes[1],
            event_mapping_id: eventMappingId,
            events
          }
        }
      },
      payload: { orchestrationId, versionId, events }
    }
  });
};

// TODO: If we add configurable reenterable or interval_minutes, remove those values from this
// As of now, some existing orchestrations don't have these values set, we need them
export const updateOrchestrationLimit = (
  orgId: number,
  orchestrationId: number,
  reEntryTimes: number | null
) => (dispatch: Function, getState: Function) => {
  const {
    api: { orchestrationsById: { [orchestrationId]: orch } }
  } = getState();
  const maxTimes = reEntryTimes === null ? null : Number(reEntryTimes) + 1;
  const reenterable = maxTimes !== 1;
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}`,
      types: [
        Types.UPDATE_ORCH_LIMIT_REQUEST,
        Types.UPDATE_ORCH_LIMIT_SUCCESS,
        Types.UPDATE_ORCH_LIMIT_FAILURE
      ],
      headers: HEADERS,
      method: 'PUT',
      body: {
        ...orch,
        config: {
          ...defaultConfig.config,
          reenterable,
          max_times: maxTimes
        }
      },
      payload: { orchestrationId, maxTimes }
    }
  });
};

export type updateOrchestrationVersionType = {
  orgId: number,
  versionId: number,
  orchestrationId: number,
  appGroupId: number,
  nodes: Object,
  apps: Array<Object>
};

export const updateOrchestrationVersion = (
  args: updateOrchestrationVersionType
) => (dispatch: Function) => {
  const { orgId, versionId, orchestrationId, appGroupId, nodes, apps } = args;
  return dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}`,
      types: [
        Types.UPDATE_ORCH_VER_REQUEST,
        Types.UPDATE_ORCH_VER_SUCCESS,
        Types.UPDATE_ORCH_VER_FAILURE
      ],
      headers: HEADERS,
      method: 'PUT',
      body: {
        nodes,
        apps,
        app_group_id: appGroupId
      },
      payload: { orgId, versionId, orchestrationId, appGroupId, nodes, apps }
    }
  });
};

export const updateOrchestrationVersionState = (
  version: OrchestrationVersion,
  orgId: number,
  orchestrationId: number,
  versionId: number,
  versionState: $Keys<typeof VERSION_STATE_MAP>
) => (dispatch: Function) =>
  dispatch({
    [SIMPLE]: {
      endpoint: `${ORCH_ENDPOINT_PATH}v1/orchestration/organization/${
        orgId
      }/orchestration/${orchestrationId}/version/${versionId}/state`,
      types: [
        Types.UPDATE_ORCH_VER_STATE_REQUEST,
        Types.UPDATE_ORCH_VER_STATE_SUCCESS,
        Types.UPDATE_ORCH_VER_STATE_FAILURE
      ],
      headers: HEADERS,
      method: 'PATCH',
      body: {
        state: versionState
      },
      payload: { orchestrationId, versionId, versionState, version }
    }
  });

type delayNodeData = {
  duration_minutes: number,
  unit: string,
  node_type: string
};

type eventNodeData = {
  display_name: string,
  event_mapping_id: number,
  expiration_minutes: number,
  node_type: string,
  unit: string,
  events: Array<{
    name: string,
    olap_id: number
  }>
};

type pushNodeData = {
  display_name: string,
  creative_ids: Array<number>,
  node_type: string
};

type modifyNodeArgs = {
  versionId: number,
  orgId: number,
  orchestrationId: number,
  appGroupId: number,
  newNodeData: delayNodeData | eventNodeData | pushNodeData,
  apps: Array<Object>,
  nodes: Object,
  nodeName: string
};

type addNodeArgs = {
  versionId: number,
  orgId: number,
  orchestrationId: number,
  appGroupId: number,
  parentName: string,
  next: string,
  nextKey: string,
  nodeData: delayNodeData | eventNodeData | pushNodeData,
  apps: Array<Object>,
  nodes: Object
};

export const modifyNode = (args: modifyNodeArgs) => (dispatch: Function) => {
  const {
    apps,
    nodes,
    versionId,
    orgId,
    orchestrationId,
    appGroupId,
    newNodeData,
    nodeName
  } = args;

  const newNodes = {
    ...nodes,
    [nodeName]: {
      ...nodes[nodeName],
      ...newNodeData
    }
  };
  return dispatch(
    updateOrchestrationVersion({
      orgId,
      versionId,
      orchestrationId,
      appGroupId,
      nodes: newNodes,
      apps
    })
  );
};

export const addNode = (args: addNodeArgs) => (dispatch: Function) => {
  const {
    apps,
    nodes,
    versionId,
    orgId,
    orchestrationId,
    appGroupId,
    parentName,
    next,
    nextKey,
    nodeData
  } = args;
  const maxId = Math.max(...Object.keys(nodes).map(Number));
  const newNodeName = maxId + 1;
  const exitNodeName = maxId + 2;
  const secondExitNodeName = maxId + 3;
  const newNodes = (() => {
    switch (nodeData.node_type) {
      case apiTypes.EVENT_LISTENER_TYPE: {
        const newNode = {
          on_seen: next || secondExitNodeName,
          on_expired: exitNodeName,
          ...nodeData
        };
        const exitNode = {
          node_type: apiTypes.EXIT_TYPE
        };

        const nodeDefinition = {
          ...nodes,
          [newNodeName]: newNode,
          [exitNodeName]: exitNode,

          [parentName]: {
            ...nodes[parentName],
            [nextKey]: newNodeName
          }
        };

        if (nodeDefinition[newNodeName].on_seen === secondExitNodeName) {
          return {
            ...nodeDefinition,
            [secondExitNodeName]: exitNode
          };
        }
        return nodeDefinition;
      }
      case apiTypes.MESSAGE_TYPE: {
        const newNode = {
          on_sent: next,
          on_not_sent: exitNodeName,
          ...nodeData
        };
        const exitNode = {
          node_type: apiTypes.EXIT_TYPE
        };
        return {
          ...nodes,
          [newNodeName]: newNode,
          [exitNodeName]: exitNode,
          [parentName]: {
            ...nodes[parentName],
            [nextKey]: newNodeName
          }
        };
      }
      case apiTypes.DELAY_TYPE: {
        const newNode = {
          next,
          ...nodeData
        };
        return {
          ...nodes,
          [newNodeName]: newNode,
          [parentName]: {
            ...nodes[parentName],
            [nextKey]: newNodeName
          }
        };
      }
      default:
        return nodes;
    }
  })();
  return dispatch(
    updateOrchestrationVersion({
      orgId,
      versionId,
      orchestrationId,
      appGroupId,
      nodes: newNodes,
      apps
    })
  );
};

export const hideErrors = () => (dispatch: Function) => {
  dispatch({
    type: Types.HIDE_ERRORS
  });
};

type deleteNodeProps = {
  version: {
    nodes: Object,
    apps: Array<Object>
  },
  nodeName: string,
  versionId: number,
  orchestrationId: number,
  orgId: number,
  appGroupId: number
};

export const deleteNode = (args: deleteNodeProps) => (dispatch: Function) => {
  const {
    version,
    nodeName,
    versionId,
    orchestrationId,
    orgId,
    appGroupId
  } = args;
  const prunedNodes = ApiUtils.deleteNode(version.nodes, nodeName);
  return dispatch(
    updateOrchestrationVersion({
      orgId,
      versionId,
      orchestrationId,
      appGroupId,
      nodes: prunedNodes,
      apps: version.apps
    })
  );
};

export const deleteAllChildren = (args: deleteNodeProps) => (
  dispatch: Function
) => {
  const {
    version,
    nodeName,
    versionId,
    orchestrationId,
    orgId,
    appGroupId
  } = args;
  const prunedNodes = ApiUtils.deleteNodeAndAllChildren(
    version.nodes,
    nodeName
  );
  return dispatch(
    updateOrchestrationVersion({
      orgId,
      versionId,
      orchestrationId,
      appGroupId,
      nodes: prunedNodes,
      apps: version.apps
    })
  );
};

export const deleteAllExceptBranch = (args: {
  version: {
    nodes: Object,
    apps: Array<Object>
  },
  nodeName: string,
  branchNameToKeep: string,
  versionId: number,
  orchestrationId: number,
  orgId: number,
  appGroupId: number
}) => (dispatch: Function) => {
  const {
    version,
    nodeName,
    branchNameToKeep,
    versionId,
    orchestrationId,
    orgId,
    appGroupId
  } = args;
  const prunedNodes = ApiUtils.deleteBranchesExceptOne(
    version.nodes,
    nodeName,
    branchNameToKeep
  );
  return dispatch(
    updateOrchestrationVersion({
      orgId,
      versionId,
      orchestrationId,
      appGroupId,
      nodes: prunedNodes,
      apps: version.apps
    })
  );
};

export default getOrchestrations;



// WEBPACK FOOTER //
// ./src/actions/api.js